import { animated, useInView, useSpring } from '@react-spring/web';
import { Pressable, StyleSheet, Text, View } from 'react-native';
import { useNavigate } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import { IThemeContext, useThemeContext, useWebSocketContext } from '../../contexts';
import CardSuccessIcon from '../../components/Icons/CardSuccessIcon';
import AcceptCredentialOfferAnimation from '../../components/animation/lib/acceptCredentialOfferAnimation';
import { CredentialExchangeRecord } from '../../types/credentials';
import { useCredentialData } from '../../contexts';
interface OfferSuccessProps {
  credential: CredentialExchangeRecord;
}
/**
 * OfferSuccess is a component that displays a success message after an offer has been accepted.
 *
 * @return {React.FC}
 */
const OfferSuccess: React.FC<OfferSuccessProps> = ({ credential }) => {
  const navigate = useNavigate();
  const { theme } = useThemeContext();
  const styles = createOfferSuccessStyles(theme);
  const { lastMessage } = useWebSocketContext();
  const [complete, setComplete] = useState(false);
  const { removeOffer, addCredential } = useCredentialData();

  useEffect(() => {
    const timeout = setTimeout(() => {
      setComplete(true);
    }, 5000);

    if (lastMessage?.data === 'process-complete') {
      clearTimeout(timeout);
      setComplete(true);
    }

    return () => clearTimeout(timeout);
  }, [lastMessage]);

  useEffect(() => {
    return () => {
      removeOffer(credential);
    };
  }, []);

  // Icon Animation
  const [iconAnim, iconAnimStyle] = useInView(() => ({
    from: {
      opacity: 0,
      transform: 'translateY(40px)'
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)'
    }
  }));

  // Text Animation
  const [textAnim, textAnimStyle] = useInView(() => ({
    from: {
      opacity: 0,
      transform: 'translateY(40px)'
    },
    to: {
      opacity: 1,
      transform: 'translateY(0)',
      delay: 100
    }
  }));

  // Button Animation
  const buttonAnimStyle = useSpring({
    opacity: complete ? 1 : 0,
    transform: complete ? 'translateY(0)' : 'translateY(40px)',
    delay: 100
  });
  return (
    <>
      {complete ? (
        <View style={styles.emptyListStyle}>
          <View style={{}}>
            <animated.div ref={iconAnim} style={iconAnimStyle}>
              <CardSuccessIcon iconHeight={160} iconWidth={160} />
            </animated.div>
          </View>

          <View>
            <animated.div ref={textAnim} style={textAnimStyle}>
              <View style={styles.emptyListTextStyle}>
                <Text style={styles.emptyListMessageTitleStyle}>Credential Added!</Text>
                <Text style={styles.emptyListMessageSubtitleStyle}>
                  you can view the credential in your wallet
                </Text>
              </View>
            </animated.div>
          </View>

          <View style={{ marginTop: 16 }}>
            <animated.div style={buttonAnimStyle}>
              <Pressable
                style={styles.buttonStyle}
                role="button"
                onPress={() => {
                  addCredential(credential);
                  navigate('/');
                }}>
                <Text style={styles.buttonText}>Back to wallet</Text>
              </Pressable>
            </animated.div>
          </View>
        </View>
      ) : (
        <View style={styles.emptyListStyle}>
          <View style={{}}>
            <animated.div ref={iconAnim} style={iconAnimStyle}>
              <AcceptCredentialOfferAnimation />
            </animated.div>
          </View>

          <View>
            <animated.div ref={textAnim} style={textAnimStyle}>
              <View style={styles.emptyListTextStyle}>
                <Text style={styles.emptyListMessageTitleStyle}>Adding your credential... </Text>
                <Text style={styles.emptyListMessageSubtitleStyle}>please wait</Text>
              </View>
            </animated.div>
          </View>
        </View>
      )}
    </>
  );
};
export default OfferSuccess;

const createOfferSuccessStyles = (theme: IThemeContext['theme']) =>
  StyleSheet.create({
    emptyListStyle: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 36,
      flex: 1,
      width: '100%',
      height: '100%',
      padding: 36
    },
    emptyListTextStyle: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      flex: 1
    },
    emptyListMessageTitleStyle: {
      fontSize: 24,
      fontWeight: 700,
      textAlign: 'center'
    },
    emptyListMessageSubtitleStyle: {
      fontSize: 18,
      color: '#999',
      textAlign: 'center'
    },
    onboardButton: {
      paddingTop: 16,
      paddingRight: 24,
      paddingBottom: 16,
      paddingLeft: 24,
      backgroundColor: 'rgb(41, 45, 50)',
      borderRadius: 6,
      color: '#fff',
      textAlign: 'center',
      boxShadow: '1px 2px 4px rgba(41, 45, 50, 0.5)'
    },
    onboardButtonText: {
      color: 'inherit',
      fontSize: 16
    },
    buttonStyle: {
      paddingTop: 16,
      paddingRight: 24,
      paddingBottom: 16,
      paddingLeft: 24,
      borderRadius: 6,
      backgroundColor: theme.primary,
      color: '#fff',
      textAlign: 'center',
      boxShadow: '1px 2px 4px rgba(41, 45, 50, 0.5)'
    },
    buttonText: {
      textAlign: 'center',
      color: 'inherit',
      width: 350,
      fontSize: 16,
      fontWeight: 600
    }
  });
